/**
 * CardOutlineEditor — 정지 사진 위 카드 외곽선 편집기 (라벨링 화면).
 *
 * card-match-testapp 편집기 포팅 + 2026-08-01 라벨링 UX 재설계 + 2026-08-07 4코너 전환:
 *  - 좌표는 전부 **원본 이미지 픽셀** — 캔버스 좌표는 그리기/히트테스트 시에만 변환.
 *  - **핸들은 가상 코너 4개** — 포켓몬 카드는 코너 라운드가 규격이라 접점 8개를 따로
 *    만질 이유가 없다(매칭 warp 도 가상 코너 4점만 쓴다). outline prop/onChange 는
 *    라벨 스키마 v3 그대로 **8접점** 유지 — 코너 드래그 시 outlineFromCorners 로 역생성.
 *  - **cover-fit**: 라이브 프리뷰와 같은 매핑으로 사진을 깔아 장면 연속성 유지.
 *  - **가이드 고정 딤**: 라이브에서 보던 가이드 박스 밖은 검정 80% — 안쪽만 보인다.
 *  - **이중 기준선**: 모델(테두리 인식) 기준선은 읽기 전용(초록), 사용자 라벨링 선은
 *    핸들 드래그 가능(보라). 코너 점 색은 카드 위(주황)/아래(하늘) 모서리 구분.
 *  - 포인터 리스너는 document capture 단계에 단다 — iOS WKWebView 에서 캔버스 하단
 *    ~44pt 터치가 다음 형제 요소로 스냅되어 캔버스 리스너가 안 오는 버그 대응
 *    (원본 main.ts 주석 검증됨). setPointerCapture 는 의도적으로 미사용.
 *  - 라운드 사각: 변 직선 + 코너 2차 베지어(가상 코너 = 제어점).
 */
import { useCallback, useEffect, useRef } from "react";
import {
  buildOutlinePath,
  outlineFromCorners,
  virtualCorners,
  type Pt,
} from "./outlineGeometry";
import { computeCardGuide } from "./scanGuide";
import {
  CORNER_TOP_COLOR,
  CORNER_BOTTOM_COLOR,
  MODEL_LINE_COLOR,
  USER_LINE_COLOR,
  isTopCornerIndex,
} from "./scanColors";

const HANDLE_R = 26; // 히트 반경 (CSS px — dpr 곱해서 사용)
const HANDLE_DRAW_R = 11; // 그리기 반경 (코너당 1핸들이라 8접점 시절보다 크게)
const MODEL_DOT_R = 5; // 모델 기준선 점 (읽기 전용 — 핸들보다 작게)
const LOUPE_R = 76;
const LOUPE_ZOOM = 3;

interface Props {
  /** convertFileSrc 처리된 사진 URL */
  imageSrc: string;
  /** 원본 이미지 픽셀 크기 */
  naturalWidth: number;
  naturalHeight: number;
  /** 사용자 라벨링 8접점 (원본 이미지 픽셀) — 핸들은 가상 코너 4개, 값은 v3 8접점 유지 */
  outline: Pt[];
  /** 모델(테두리 인식) 기준선 8접점 (원본 이미지 픽셀) — 읽기 전용 */
  modelOutline?: Pt[] | null;
  onChange: (outline: Pt[]) => void;
  className?: string;
}

export default function CardOutlineEditor({
  imageSrc,
  naturalWidth,
  naturalHeight,
  outline,
  modelOutline,
  onChange,
  className,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragIdxRef = useRef(-1);
  const outlineRef = useRef(outline);
  outlineRef.current = outline;
  const modelOutlineRef = useRef(modelOutline);
  modelOutlineRef.current = modelOutline;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // cover-fit: 원본 → 캔버스 backing-store 픽셀 (라이브 프리뷰와 동일 매핑 — 장면 연속)
  const fitScale = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return { s: 1, ox: 0, oy: 0 };
    const s = Math.max(canvas.width / naturalWidth, canvas.height / naturalHeight);
    return {
      s,
      ox: (canvas.width - naturalWidth * s) / 2,
      oy: (canvas.height - naturalHeight * s) / 2,
    };
  }, [naturalWidth, naturalHeight]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !img.complete) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = devicePixelRatio;
    const { s, ox, oy } = fitScale();
    const toCanvas = (p: Pt): Pt => ({ x: ox + p.x * s, y: oy + p.y * s });

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, ox, oy, naturalWidth * s, naturalHeight * s);

    const pts = outlineRef.current.map(toCanvas);
    if (pts.length !== 8) return;

    // 가이드 고정 딤 — 라이브에서 보던 가이드 박스 밖은 검정 80% (사용자 지시).
    // 가이드는 스틸 정규화로 계산(computeCardGuide 스케일 불변) → 캔버스로 변환.
    const guide = computeCardGuide(canvas.width, canvas.height, naturalWidth, naturalHeight);
    const g = guide.map((p) => toCanvas({ x: p.x * naturalWidth, y: p.y * naturalHeight }));
    const gLeft = Math.min(g[0].x, g[3].x);
    const gTop = Math.min(g[0].y, g[1].y);
    const gRight = Math.max(g[1].x, g[2].x);
    const gBottom = Math.max(g[2].y, g[3].y);
    const dim = new Path2D();
    dim.rect(0, 0, canvas.width, canvas.height);
    dim.rect(gLeft, gTop, gRight - gLeft, gBottom - gTop);
    // 가이드 밖 딤 — 0.8 은 배경이 통째로 묻혀 카드가 어디 있는지 가늠이 안 됐다
    // (사용자 제보). 가이드를 강조하되 배경 형태는 남는 정도로 낮춘다.
    ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
    ctx.fill(dim, "evenodd");
    // 가이드 테두리 (라이브와 동일한 기준선 유지)
    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    ctx.lineWidth = 2 * dpr;
    ctx.strokeRect(gLeft, gTop, gRight - gLeft, gBottom - gTop);

    // ── 모델(테두리 인식) 기준선 — 읽기 전용, 초록 라인 + 가상 코너 4점 (위/아래 색)
    const model = modelOutlineRef.current;
    if (model && model.length === 8) {
      const mPts = model.map(toCanvas);
      const mCorners = virtualCorners(model).map(toCanvas);
      const mPath = buildOutlinePath(mPts, mCorners);
      ctx.strokeStyle = MODEL_LINE_COLOR;
      ctx.lineWidth = 2 * dpr;
      ctx.stroke(mPath);
      for (let i = 0; i < 4; i++) {
        ctx.beginPath();
        ctx.arc(mCorners[i].x, mCorners[i].y, MODEL_DOT_R * dpr, 0, Math.PI * 2);
        ctx.fillStyle = isTopCornerIndex(i) ? CORNER_TOP_COLOR : CORNER_BOTTOM_COLOR;
        ctx.fill();
        ctx.strokeStyle = MODEL_LINE_COLOR;
        ctx.lineWidth = 1.5 * dpr;
        ctx.stroke();
      }
    }

    // ── 사용자 라벨링 선 — 보라 라인 + 가상 코너 4핸들 (위=주황/아래=하늘)
    const corners = virtualCorners(outlineRef.current).map(toCanvas);
    const path = buildOutlinePath(pts, corners);
    ctx.strokeStyle = USER_LINE_COLOR;
    ctx.lineWidth = 3 * dpr;
    ctx.stroke(path);

    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.arc(corners[i].x, corners[i].y, HANDLE_DRAW_R * dpr, 0, Math.PI * 2);
      ctx.fillStyle =
        i === dragIdxRef.current
          ? "#ffffff"
          : isTopCornerIndex(i)
            ? CORNER_TOP_COLOR
            : CORNER_BOTTOM_COLOR;
      ctx.fill();
      ctx.strokeStyle = "#101013";
      ctx.lineWidth = 2 * dpr;
      ctx.stroke();
    }

    // 루페 — 드래그 중인 코너 주변 확대
    const dragIdx = dragIdxRef.current;
    if (dragIdx >= 0) {
      const target = corners[dragIdx];
      const r = LOUPE_R * dpr;
      const left = target.x < canvas.width / 2;
      const cx = left ? canvas.width - r - 12 * dpr : r + 12 * dpr;
      const cy = r + 12 * dpr;
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.clip();
      ctx.fillStyle = "#000";
      ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
      const zs = s * LOUPE_ZOOM;
      const imgPt = virtualCorners(outlineRef.current)[dragIdx];
      ctx.drawImage(
        img,
        cx - imgPt.x * zs,
        cy - imgPt.y * zs,
        naturalWidth * zs,
        naturalHeight * zs,
      );
      // 십자선
      ctx.strokeStyle = "#ff4d4d";
      ctx.lineWidth = 1.5 * dpr;
      ctx.beginPath();
      ctx.moveTo(cx - 12 * dpr, cy);
      ctx.lineTo(cx + 12 * dpr, cy);
      ctx.moveTo(cx, cy - 12 * dpr);
      ctx.lineTo(cx, cy + 12 * dpr);
      ctx.stroke();
      ctx.restore();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = "#e8e8ea";
      ctx.lineWidth = 2 * dpr;
      ctx.stroke();
    }
  }, [fitScale, naturalWidth, naturalHeight]);

  // 이미지 로드
  useEffect(() => {
    const img = new Image();
    img.onload = () => draw();
    img.src = imageSrc;
    imgRef.current = img;
    return () => {
      imgRef.current = null;
    };
  }, [imageSrc, draw]);

  // 캔버스 backing store 동기화 + outline 변경 시 재그리기
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const sync = () => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const w = Math.round(rect.width * devicePixelRatio);
      const h = Math.round(rect.height * devicePixelRatio);
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;
      draw();
    };
    const ro = new ResizeObserver(sync);
    ro.observe(canvas);
    sync();
    return () => ro.disconnect();
  }, [draw]);

  useEffect(() => {
    draw();
  }, [outline, modelOutline, draw]);

  // 포인터 — document capture 단계 (iOS 하단 44pt 스냅 버그 대응, 원본 검증 방식)
  useEffect(() => {
    const canvasPos = (ev: PointerEvent): Pt | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const r = canvas.getBoundingClientRect();
      return {
        x: ((ev.clientX - r.left) / r.width) * canvas.width,
        y: ((ev.clientY - r.top) / r.height) * canvas.height,
      };
    };
    const inEditor = (ev: PointerEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      const r = canvas.getBoundingClientRect();
      return (
        ev.clientX >= r.left && ev.clientX <= r.right &&
        ev.clientY >= r.top && ev.clientY <= r.bottom
      );
    };

    const onDown = (ev: PointerEvent) => {
      if (!inEditor(ev)) return;
      // ⚠ document 에 capture 로 붙어 있어 **위에 덮인 UI 보다 먼저** 이벤트를 받는다.
      // 그대로 두면 결과 패널·버튼을 눌러도 아래 4점 핸들이 잡혀 외곽선이 끌려간다
      // (사용자 제보). 눌린 지점의 최상위 요소가 캔버스가 아니면 편집이 아니다.
      // ⚠ pointerdown 에서만 막는다 — 드래그가 시작된 뒤에는 캔버스 밖으로 나가도
      // 계속 따라가야 하므로 move/up 은 그대로 capture 로 받는다.
      if (ev.target !== canvasRef.current) return;
      const pos = canvasPos(ev);
      if (!pos) return;
      ev.preventDefault();
      const { s, ox, oy } = fitScale();
      let best = -1;
      let bestDist = HANDLE_R * devicePixelRatio;
      virtualCorners(outlineRef.current).forEach((p, i) => {
        const cp = { x: ox + p.x * s, y: oy + p.y * s };
        const d = Math.hypot(cp.x - pos.x, cp.y - pos.y);
        if (d < bestDist) {
          best = i;
          bestDist = d;
        }
      });
      dragIdxRef.current = best;
      if (best >= 0) draw();
    };
    const onMove = (ev: PointerEvent) => {
      const dragIdx = dragIdxRef.current;
      if (dragIdx < 0) return;
      const pos = canvasPos(ev);
      if (!pos) return;
      ev.preventDefault();
      const { s, ox, oy } = fitScale();
      // 코너를 옮기고 8접점은 역생성 — 라운드 접점 위치는 ROUND_INSET 고정값으로 충분
      // (카드 코너 라운드는 물리 규격이라 개별 조정의 실익이 없다)
      const corners = virtualCorners(outlineRef.current);
      corners[dragIdx] = {
        x: Math.min(Math.max((pos.x - ox) / s, 0), naturalWidth),
        y: Math.min(Math.max((pos.y - oy) / s, 0), naturalHeight),
      };
      onChangeRef.current(outlineFromCorners(corners));
    };
    const endDrag = () => {
      if (dragIdxRef.current < 0) return;
      dragIdxRef.current = -1;
      draw();
    };

    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("pointermove", onMove, true);
    document.addEventListener("pointerup", endDrag, true);
    document.addEventListener("pointercancel", endDrag, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("pointermove", onMove, true);
      document.removeEventListener("pointerup", endDrag, true);
      document.removeEventListener("pointercancel", endDrag, true);
    };
  }, [draw, fitScale, naturalWidth, naturalHeight]);

  return <canvas ref={canvasRef} className={className} style={{ touchAction: "none" }} />;
}
